import React, { useState, useRef, useEffect } from 'react';
import { Typography, TextField, Button, Avatar, IconButton, Alert, FormControlLabel, Checkbox } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import { useNavigate } from 'react-router-dom';

import { useUser } from '../../UserContext';
import { useAuth } from '../../AuthContext';
import { updateProfile } from '../../services/userService';
import { Utils } from '../../utils/utils';

import { uploadFile } from '../../services/uploadService';
import { aws_base_path } from '../../config/config';

const UserProfilePage = () => {
  const { user, setUser } = useUser();
  const { auth } = useAuth();
  const navigate = useNavigate();
  const [userToEdit, setUserToEdit] = useState({ ...user });
  const fileInputRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(user?.profilePic ? aws_base_path + user.profilePic : '');
  const [uploadStatus, setUploadStatus] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [customerReadyDialogOpen, setCustomerReadyDialogOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: ''
  });
  const isChefUser = Utils.isChef(user);
  const isCustomerUser = Utils.isCustomer(user);

  // Update local state when user context changes, and run validation immediately
  // so missing required fields are flagged on first load
  useEffect(() => {
    setUserToEdit({ ...user });
    setPreviewUrl(user?.profilePic ? aws_base_path + user.profilePic : '');
    setValidationErrors({
      firstName: validateField('firstName', user?.firstName),
      lastName: validateField('lastName', user?.lastName),
      email: validateField('email', user?.email),
      phoneNumber: validateField('phoneNumber', user?.phoneNumber),
    });
  }, [user]);

  useEffect(() => {
    const scrollToHashSection = () => {
      const hash = window.location.hash;

      if (!hash) {
        return;
      }

      const targetSection = document.getElementById(hash.replace('#', ''));

      if (targetSection && typeof targetSection.scrollIntoView === 'function') {
        targetSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };

    scrollToHashSection();
    const timeoutId = setTimeout(scrollToHashSection, 120);
    window.addEventListener('hashchange', scrollToHashSection);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('hashchange', scrollToHashSection);
    };
  }, [user]);

  const stripPhoneNumber = (value) => {
    if (!value) return '';
    return value.replace(/\D/g, '');
  };

  const validateField = (name, value) => {
    let errorMsg = '';
    
    if (name === 'firstName') {
      if (!value || value.trim() === '') {
        errorMsg = 'First name is required';
      } else if (!/^[a-zA-Z\s]+$/.test(value)) {
        errorMsg = 'First name can only contain letters';
      }
    } else if (name === 'lastName') {
      if (!value || value.trim() === '') {
        errorMsg = 'Last name is required';
      } else if (!/^[a-zA-Z\s]+$/.test(value)) {
        errorMsg = 'Last name can only contain letters';
      }
    } else if (name === 'email') {
      if (!value || value.trim() === '') {
        errorMsg = 'Email is required';
      } else if (!Utils.validateEmailFormat(value)) {
        errorMsg = 'Please enter a valid email address';
      }
    } else if (name === 'phoneNumber') {
      if (!value || value.trim() === '') {
        errorMsg = 'Phone number is required';
      } else {
        // Strip non-digit characters to count actual digits
        const digitsOnly = value.replace(/\D/g, '');
        if (digitsOnly.length < 10) {
          errorMsg = 'Phone number is incomplete';
        } else if (digitsOnly.length > 10) {
          errorMsg = 'Phone number is too long';
        }
      }
    }
    
    return errorMsg;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    let valueToStore = value;
    
    // For phone number, store plain digits only
    if (name === 'phoneNumber') {
      valueToStore = stripPhoneNumber(value);
    }
    
    setUserToEdit((prevUser) => ({
      ...prevUser,
      [name]: valueToStore,
    }));
    
    // Validate field on change
    const errorMsg = validateField(name, valueToStore);
    setValidationErrors((prev) => ({
      ...prev,
      [name]: errorMsg
    }));
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
        setUserToEdit((prevUser) => ({
          ...prevUser,
          profilePic: reader.result,
        }));
      };
      reader.readAsDataURL(file);

      // storage file
      handleUpload(file);
    }
  };

  const handleUpload = (selectedFile) => {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('metadata', JSON.stringify(
        { 
          chefId: user?.chef?.id,
          customerId: user?.customer?.id,
          kitchenId: 0, 
          menuItemId: 0, 
          pictureType: isChefUser ? 'chefProfilePic' : 'customerProfilePic'
        }
      ));
  
      uploadFile(formData, auth).then((response) => {
        if (response) {
          setUserToEdit((prevUser) => ({
            ...prevUser,
            profilePic: response.imageThumbUrl,
          }));
          setPreviewUrl(aws_base_path + response.imageThumbUrl);
        } else {
          setUploadStatus('Error uploading file');
        }
      })
      .catch((error) => {
        console.error('Error uploading file:', error);
        setUploadStatus('Error uploading file');
      });
  };

  const handleCancel = () => {
    setUserToEdit({ ...user });
    setPreviewUrl(user?.profilePic ? aws_base_path + user.profilePic : '');
    setError(null);
    setSuccess(false);
    // Re-validate against the saved user so missing fields remain flagged
    setValidationErrors({
      firstName: validateField('firstName', user?.firstName),
      lastName: validateField('lastName', user?.lastName),
      email: validateField('email', user?.email),
      phoneNumber: validateField('phoneNumber', user?.phoneNumber),
    });
  };

  const validateForm = () => {
    const errors = {
      firstName: validateField('firstName', userToEdit.firstName),
      lastName: validateField('lastName', userToEdit.lastName),
      email: validateField('email', userToEdit.email),
      phoneNumber: validateField('phoneNumber', userToEdit.phoneNumber)
    };
    
    setValidationErrors(errors);
    
    // Return true if no errors
    return !Object.values(errors).some(error => error !== '');
  };

  const hasChanges = () => {
    // Compare relevant fields to check if data has changed
    return userToEdit.firstName !== user.firstName ||
           userToEdit.lastName !== user.lastName ||
           userToEdit.nickName !== user.nickName ||
           userToEdit.email !== user.email ||
           userToEdit.phoneNumber !== user.phoneNumber ||
           userToEdit.selfIntroduction !== user.selfIntroduction ||
           userToEdit.profilePic !== user.profilePic;
  };

  const chefInfoHasUpdates = () => {
    // Compare chef relevant fields to check if data has changed
    return userToEdit.chef?.complianceAcknowledgment !== user.chef?.complianceAcknowledgment ||
           userToEdit.chef?.photoId !== user.chef?.photoId;
  };

  const customerInfoHasUpdates = () => {
    // Compare customer relevant fields to check if data has changed
    return userToEdit.customer?.complianceAcknowledgment !== user.customer?.complianceAcknowledgment;
  };

  const isFormValid = () => {
    // Check if all required fields are filled and have no validation errors
    return userToEdit.firstName && 
           userToEdit.lastName && 
           userToEdit.email && 
           userToEdit.phoneNumber &&
           userToEdit.profilePic &&
           !validationErrors.firstName &&
           !validationErrors.lastName &&
           !validationErrors.email &&
           !validationErrors.phoneNumber &&
           (hasChanges() || chefInfoHasUpdates() || customerInfoHasUpdates()); // Only enable if there are actual changes
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setCustomerReadyDialogOpen(false);

    const wasCustomerReady = Utils.isCustomerReady(user);

    if (!validateForm()) {
      return;
    }

    updateProfile(userToEdit, auth).then((updatedUser) => {
      if (updatedUser) {
        setUser(updatedUser);
        setSuccess(true);

        if (isCustomerUser && !wasCustomerReady && Utils.isCustomerReady(updatedUser)) {
          setCustomerReadyDialogOpen(true);
        }
      } else {
        setError('Failed to update profile');
      }
    }).catch((error) => {
      console.error('Error updating profile:', error);
      setError('Sorry, an error occurred while saving!');
    });
  };

  return (

        <div style={{ display: 'flex', maxWidth: 520, flexDirection: 'column'}}>

            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
                <Typography variant="h5">
                    User Profile
                </Typography>

                <div style={{ position: 'relative', display: 'inline-block' }}>
                    <Avatar 
                        data-testid="user-avatar"
                        src={previewUrl} 
                        alt={userToEdit?.name} 
                        sx={{ 
                            width: 90, 
                            height: 90, 
                            mb: 2,
                            cursor: 'pointer',
                            border: !userToEdit?.profilePic ? '1px solid' : 'none',
                            borderColor: !userToEdit?.profilePic ? 'error.main' : 'transparent',
                            '&:hover': {
                                opacity: 0.8
                            }
                        }}
                        onClick={handleAvatarClick}
                    />
                    <IconButton
                        sx={{
                            position: 'absolute',
                            bottom: 16,
                            right: 0,
                            backgroundColor: 'primary.main',
                            color: 'white',
                            width: 32,
                            height: 32,
                            '&:hover': {
                                backgroundColor: 'primary.dark'
                            }
                        }}
                        onClick={handleAvatarClick}
                    >
                        <CameraAltIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                    <input
                        ref={fileInputRef}
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleFileChange}
                        data-testid="input-profile-pic"
                    />
                    {uploadStatus && <p>{uploadStatus}</p>}
                    {!userToEdit?.profilePic && (
                        <Typography
                            variant="caption"
                            data-testid="missing-profile-pic-label"
                            sx={{ color: 'error.main', display: 'block', textAlign: 'center', mt: 0.5 }}
                        >
                            Profile picture required
                        </Typography>
                    )}
                </div>
            </div>
            
            <form onSubmit={handleSubmit}>

            {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ mt: 2, mb: 2 }} data-testid="error-alert">
                    Sorry, an error occurred while saving!
                </Alert>
            )}

            {success && (
                <Alert severity="success" onClose={() => setSuccess(false)} sx={{ mt: 2, mb: 2 }} data-testid="success-alert">
                    Profile updated successfully!
                </Alert>
            )}

            <Dialog
              open={customerReadyDialogOpen}
              onClose={() => setCustomerReadyDialogOpen(false)}
              aria-labelledby="customer-ready-dialog-title"
            >
              <DialogTitle id="customer-ready-dialog-title">
                You are now ready to explore kitchens
              </DialogTitle>
              <DialogContent>
                <Typography variant="body2" color="text.secondary">
                  Your customer profile is complete. You can stay on this page or explore kitchens now.
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => setCustomerReadyDialogOpen(false)}
                  style={{ textTransform: 'none' }}
                >
                  Stay in this page
                </Button>
                <Button
                  variant="contained"
                  onClick={() => navigate('/list')}
                  style={{ textTransform: 'none' }}
                >
                  Explore kitchens now
                </Button>
              </DialogActions>
            </Dialog>

            <TextField
                label="First Name"
                name="firstName"
                value={userToEdit.firstName || ''}
                onChange={handleChange}
                fullWidth
                margin="normal"
                variant="outlined"
                required
                error={!!validationErrors.firstName}
                helperText={validationErrors.firstName}
                inputProps={{ 'data-testid': 'input-firstName' }}
            />

            <TextField
                label="Last Name"
                name="lastName"
                value={userToEdit.lastName || ''}
                onChange={handleChange}
                fullWidth
                margin="normal"
                variant="outlined"
                required
                error={!!validationErrors.lastName}
                helperText={validationErrors.lastName}
                inputProps={{ 'data-testid': 'input-lastName' }}
            />

            <TextField
                label="Nickname"
                name="nickName"
                value={userToEdit.nickName || ''}
                onChange={handleChange}
                fullWidth
                margin="normal"
                variant="outlined"
                inputProps={{ 'data-testid': 'input-nickName' }}
            />

            <TextField
                label="Email"
                name="email"
                value={userToEdit.email || ''}
                onChange={handleChange}
                fullWidth
                margin="normal"
                variant="outlined"
                required
                error={!!validationErrors.email}
                inputProps={{ 'data-testid': 'input-email' }}
                disabled={true}
                helperText="Email cannot be changed from this page"
                sx={{
                  '& .MuiInputBase-root': {
                    backgroundColor: '#f5f5f5',
                  }
                }}
            />

            <TextField
                label="Password"
                name="passwrd"
              value={userToEdit.password || '••••••••'}
                type='password'
                onChange={handleChange}
                fullWidth
                margin="normal"
                variant="outlined"
                inputProps={{ 'data-testid': 'input-password' }}
                disabled={true}
                helperText="Password cannot be changed from this page"
                sx={{
                    '& .MuiInputBase-root': {
                        backgroundColor: '#f5f5f5',
                    }
                }}
            />

            <TextField
                label="Phone"
                name="phoneNumber"
                value={Utils.formatPhoneNumber(userToEdit.phoneNumber) || ''}
                onChange={handleChange}
                fullWidth
                margin="normal"
                variant="outlined"
                required
                error={!!validationErrors.phoneNumber}
                helperText={validationErrors.phoneNumber}
                placeholder="(###) ###-####"
                inputProps={{ 'data-testid': 'input-phone' }}
            />

            <TextField
                label="Self Introduction"
                name="selfIntroduction"
                value={userToEdit?.selfIntroduction || ''}
                onChange={handleChange}
                fullWidth
                margin="normal"
                variant="outlined"
                multiline
                rows={4}
                placeholder="Tell us a bit about yourself..."
                helperText={`${(userToEdit?.selfIntroduction || '').length}/500 characters`}
                inputProps={{ maxLength: 500, 'data-testid': 'input-selfIntroduction' }}
            />

            {isChefUser && (
              <div id="compliance-acknowledgment-section" style={{
                    border: '2px solid #1976d2',
                    borderRadius: '4px',
                    padding: '16px',
                    paddingRight: '24px',
                    marginTop: 16,
                    marginBottom: 8,
                    backgroundColor: user?.chef?.complianceAcknowledgment ? '#f5f5f5' : '#e3f2fd'
                }}>
                    <Typography 
                        variant="subtitle2" 
                        style={{ 
                            fontWeight: 600, 
                            marginBottom: 8,
                            color: '#1976d2'
                        }}
                    >
                        Regulatory Compliance Acknowledgment
                    </Typography>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={userToEdit?.chef?.complianceAcknowledgment || false}
                                disabled={(user?.chef?.complianceAcknowledgment) || false}
                                onChange={(e) => {
                                    setUserToEdit({
                                        ...userToEdit,
                                        chef: {
                                            ...userToEdit.chef,
                                            complianceAcknowledgment: e.target.checked
                                        }
                                    });
                                }}
                                name="complianceAcknowledgment"
                                color="primary"
                                inputProps={{ 'data-testid': 'input-compliance-acknowledgment' }}
                            />
                        }
                        label={
                            <Typography 
                                variant="body2" 
                                style={{ 
                                    fontSize: '0.875rem', 
                                    lineHeight: 1.5, 
                                    textAlign: 'justify',
                                    color: user?.chef?.complianceAcknowledgment ? '#9e9e9e' : 'inherit'
                                }}
                            >
                                I hereby acknowledge and certify that I am solely responsible for compliance with all applicable 
                                federal, state, and local laws, regulations, and ordinances governing cottage food operations, 
                                including but not limited to food safety standards, licensing requirements, and permitted food 
                                items. I acknowledge that failure to comply with applicable regulations may result in suspension 
                                or termination of my account. By checking this box, I accept full legal responsibility for the 
                                operation of my cottage food business.
                            </Typography>
                        }
                        style={{ alignItems: 'flex-start', margin: 0 }}
                    />
                </div>
            )}

              {isCustomerUser && (
                <div id="compliance-acknowledgment-section" style={{
                  border: '2px solid #1976d2',
                  borderRadius: '4px',
                  padding: '16px',
                  paddingRight: '24px',
                  marginTop: 16,
                  marginBottom: 8,
                  backgroundColor: user?.customer?.complianceAcknowledgment ? '#f5f5f5' : '#e3f2fd'
                }}>
                  <Typography 
                    variant="subtitle2" 
                    style={{ 
                      fontWeight: 600, 
                      marginBottom: 8,
                      color: '#1976d2'
                    }}
                  >
                    Customer Responsibility Acknowledgment
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={userToEdit?.customer?.complianceAcknowledgment || false}
                        disabled={(user?.customer?.complianceAcknowledgment) || false}
                        onChange={(e) => {
                          setUserToEdit({
                            ...userToEdit,
                            customer: {
                              ...userToEdit.customer,
                              complianceAcknowledgment: e.target.checked
                            }
                          });
                        }}
                        name="complianceAcknowledgment"
                        color="primary"
                        inputProps={{ 'data-testid': 'input-compliance-acknowledgment' }}
                      />
                    }
                    label={
                      <Typography 
                        variant="body2" 
                        style={{ 
                          fontSize: '0.875rem', 
                          lineHeight: 1.5, 
                          textAlign: 'justify',
                          color: user?.customer?.complianceAcknowledgment ? '#9e9e9e' : 'inherit'
                        }}
                      >
                        By creating an account or using this platform, you acknowledge and agree that you are at least eighteen (18) years of age and legally capable of entering into this Agreement. You further agree to use the platform only for lawful, legitimate purposes and in accordance with all applicable terms, policies, and community standards.
                        <br/><br/>
                        You agree not to engage in fraudulent, abusive, or disruptive behavior, including but not limited to creating false identities, submitting fake or misleading orders, or otherwise attempting to manipulate or interfere with the proper functioning of the platform. Any violation of these obligations may result in immediate suspension or termination of your account, at the platform’s sole discretion, without prior notice.
                      </Typography>
                    }
                    style={{ alignItems: 'flex-start', margin: 0 }}
                  />
                </div>
              )}

                <div style={{ display: 'flex', gap:8, justifyContent: 'right', marginTop: 16}}>
                    <Button 
                        style={{ textTransform: 'none'}} 
                        type="button" 
                        variant="contained" 
                        color="inherit"
                        onClick={handleCancel}
                        data-testid="cancel-button"
                      disabled={!hasChanges() && !chefInfoHasUpdates() && !customerInfoHasUpdates()} // Disable if no changes
                    >
                        Cancel
                    </Button>
                    <Button 
                        style={{ textTransform: 'none'}} 
                        type="submit" 
                        variant="contained" 
                        color="primary"
                        disabled={!isFormValid()}
                        data-testid="save-changes-button"
                    >
                        Save Changes
                    </Button>
                </div>
            </form>
        </div>

  );
};

export default UserProfilePage;
